/**
 * The one place the app turns "these reads are open" into "draw the skeleton".
 *
 * The policy itself is `loadingGate`, which is pure and tested; this owns the clock and the timer
 * and decides nothing. Same split as `screenMotion` / `useScreenMotion` beside it, for the same
 * reason: a rule you can only exercise by rendering is a rule nobody exercises.
 *
 * ── Read this before using it ─────────────────────────────────────────────────────────────────
 * The gate hides the SKELETON, never the box. A card whose reads are open holds the same height in
 * all three of its short states — the wait before the skeleton appears, the skeleton, and the
 * empty answer — so what this switches on and off cannot move anything. Applying it to a card with
 * no reserve turns a jump into two jumps.
 *
 * ── The hand-over is part of the gate, because only the gate knows there was one ─────────────
 * Added 9 September 2026. A skeleton that is removed in the same frame its content appears is a
 * cut, and the owner asked for the shimmer to hand over to the content instead (motion.dev's
 * React skeleton shimmer). The class that does it is `shared/ui/skeleton.module.css`'s `.reveal`,
 * and it is handed out HERE rather than decided at a call site for one reason: the reveal is only
 * correct when a skeleton was actually drawn. A read answered inside the 150ms grace has nothing
 * to hand over from, and fading its content in would be movement on a screen that never waited.
 *
 * It is returned as the class STRING rather than a second boolean so a caller cannot apply it at
 * the wrong moment: an empty string is the whole of "not now". And it stays applied once given —
 * see the stylesheet for why taking it away again would break the one thing it has to do.
 *
 * ── The gate owns the WAIT BRANCH, not just the bars (21 September 2026) ─────────────────────
 * `waiting` is the field that makes the hand-over real, and it was added because without it the
 * gate computed the right moment and no surface used it. Every call site forked on its own read's
 * `pending`, so for an answer landing between 150 and 450ms — an ordinary network round trip — the
 * trainer saw the screen enter TWICE:
 *
 * | time | what happened |
 * |---|---|
 * | 0ms | the reads open, the reserved box stands, no bars |
 * | 150ms | the grace closes, bars appear |
 * | ~250ms | the answer lands. `pending` flips, the surface swaps to its content AT FULL OPACITY — the bars flash away (jump 1) |
 * | 450ms | the minimum life ends, the gate finally hands out `.reveal`, and the content already on screen blinks to transparent and fades back in (jump 2) |
 *
 * The 300ms minimum life was, from the surface's point of view, dead code. So a surface forks on
 * `waiting` — which stays true until the bars have actually earned their life — and `reveal` is
 * handed out in the SAME state update that retires them. The content's first render carries the
 * class, so no frame paints it at full opacity first. A surface that forks on its read's own
 * `pending` reintroduces both jumps; that is the one thing this interface exists to prevent.
 *
 * `aria-busy` follows `waiting` too, with the visually-hidden "loading" sentence beside it. The two
 * are the pair `shared/ui/skeleton.module.css` describes, and they belong to the BARS rather than to
 * the read: while the bars are still standing the region has not finished changing, so reporting it
 * settled the moment the answer landed tells a listener something they then act on. The attribute,
 * the branch and the sentence key on one value, or the card is wrong.
 * A write in flight is a different thing and keeps its own `aria-busy`.
 *
 * ── Why the state is a ref plus one PHASE object ──────────────────────────────────────────────
 * The two timestamps are the wait's MEMORY, not something a render reads: only the effect that
 * owns the timer touches them, and re-rendering on each one would be a render per timer tick for
 * no visible difference. Writing a ref from an EFFECT is allowed under the React Compiler; writing
 * one during render is not, and that is why the timestamps are never adjusted in the body of this
 * function.
 *
 * What rendering depends on is ONE object rather than two booleans, and that is load-bearing
 * rather than tidy: `visible` going false and `handingOver` going true are the same event, and as
 * two `useState` calls they are two values that a future edit could tear apart. Written together
 * in `step()` they cannot be observed apart.
 *
 * The effect is kept — deliberately — rather than moved into render. The render in which `pending`
 * flips false still draws the skeleton, because `phase.visible` is still true at that point, so
 * `waiting` is still true and no frame paints content without the class.
 */

import { useEffect, useRef, useState } from 'react';

import skel from '../ui/skeleton.module.css';

import { loadingGate } from './loadingGate';

interface WaitMemory {
  readonly pendingSince: number | null;
  readonly shownAt: number | null;
  /** Whether a skeleton has been on screen during the wait that is now ending. */
  readonly drewSkeleton: boolean;
}

/** What a surface needs to draw one wait. */
export interface SkeletonGate {
  /**
   * Whether the WAIT BRANCH belongs on screen this frame — the reads are open, or the skeleton has
   * not yet had its minimum life. This is what a surface forks on. Forking on the read's own
   * `pending` instead is the defect described in the header, and it is invisible on a local stack.
   */
  readonly waiting: boolean;
  /**
   * Whether the bars belong inside the wait. False for the first 150ms of it, so an answer that
   * arrives quickly draws a reserved box and never a flicker.
   */
  readonly skeleton: boolean;
  /**
   * The class the content that REPLACES the skeleton carries — `skel.reveal`, or `''` when there
   * was no skeleton to hand over from. Put it on the element the answer brings with it; putting it
   * on a box that was already on screen fades that box's existing ink too.
   */
  readonly reveal: string;
}

/**
 * What the surface draws, as one value. `visible` and `handingOver` are the same event seen from
 * its two sides, so they are written together and can never be read apart.
 */
interface GatePhase {
  /** Whether the bars are on screen. */
  readonly visible: boolean;
  /** Whether a skeleton has been drawn and has now left. */
  readonly handingOver: boolean;
}

/**
 * @param pending whether every read the surface draws from is still open. A section composed of
 *   several reads passes their union: a skeleton that retires when the first of three lands is a
 *   partial answer wearing the shape of a complete one.
 */
export function useSkeletonGate(pending: boolean): SkeletonGate {
  const wait = useRef<WaitMemory>({ pendingSince: null, shownAt: null, drewSkeleton: false });
  const [phase, setPhase] = useState<GatePhase>({ visible: false, handingOver: false });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const step = () => {
      const now = performance.now();
      // A wait that is only starting now cannot have drawn anything yet, and must not inherit the
      // previous wait's answer — otherwise the second read on a surface reveals without a skeleton.
      const waitIsNew = pending && wait.current.pendingSince === null;
      const pendingSince = pending ? (wait.current.pendingSince ?? now) : null;
      const verdict = loadingGate({
        pending,
        pendingSince,
        shownAt: wait.current.shownAt,
        now,
      });
      const drewSkeleton = (waitIsNew ? false : wait.current.drewSkeleton) || verdict.visible;
      wait.current = {
        pendingSince,
        shownAt: verdict.visible ? (wait.current.shownAt ?? now) : null,
        drewSkeleton,
      };
      // ONE update, and that is the point of the phase object. The hand-over is the moment the
      // skeleton LEAVES, which is not the moment the read landed: a skeleton inside its 300ms
      // minimum life is still on screen after the answer arrived. The bars retiring and the class
      // being handed out are that single moment, so the render that first draws the content is
      // also the first render that carries `.reveal`.
      setPhase({ visible: verdict.visible, handingOver: drewSkeleton && !verdict.visible });
      if (verdict.recheckAt !== null) {
        timer = setTimeout(step, Math.max(0, verdict.recheckAt - now));
      }
    };

    step();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [pending]);

  return {
    // The read is open, OR the bars have not yet had their minimum life. Either way the surface
    // draws its wait; the content waits with it rather than flashing in and fading again.
    waiting: pending || phase.visible,
    skeleton: phase.visible,
    // `&& !pending` is what stops a SECOND wait on the same surface inheriting the first one's
    // hand-over: the moment a new read opens, the class the previous answer arrived on is gone.
    reveal: phase.handingOver && !pending ? String(skel.reveal) : '',
  };
}
